/*
 * Copyright 2023 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { inspect } from "node:util";
import { ExitProcessError } from "@osdk/cli.common";
import { PalantirApiError, UnknownError } from "@osdk/shared.net.errors";
import { createFetchHeaderMutator, createFetchOrThrow } from "@osdk/shared.net.fetch";
import { consola } from "consola";
import prettyBytes from "pretty-bytes";
import { USER_AGENT } from "./UserAgent.js";
export function createFetch(tokenProvider, fetchFn = fetch) {
  return createFetchHeaderMutator(createRequestLoggingFetch(createErrorExitingFetch(createFetchOrThrow(fetchFn))), async headers => {
    const token = await tokenProvider();
    headers.set("Authorization", `Bearer ${token}`);
    headers.set("Fetch-User-Agent", USER_AGENT);
    return headers;
  });
}
function createErrorExitingFetch(fetchFn = fetch) {
  return function errorExitingFetch(input, init) {
    return fetchFn(input, init).catch(handleFetchError);
  };
}
function handleFetchError(e) {
  if (!(e instanceof PalantirApiError)) {
    throw new ExitProcessError(1, "Unexpected fetch error");
  }
  let tip;
  if (e.statusCode === 401) {
    tip = "Check your token is valid and has not expired or been disabled";
  } else if (e.statusCode === 403) {
    tip = "Check your token has the required scopes for this operation";
  } else if (e.statusCode === 404) {
    tip = "The resource may not exist or your token may not have the required scopes to load it";
  }
  let message;
  if (e.errorName === "CannotDeleteDeployedVersion") {
    message = "The site version to delete is live and cannot be deleted";
    tip = "Run the `site version set` command to change the live site version";
  } else if (e.errorName === "InvalidVersion") {
    message = "The site version is invalid and must be a valid SemVer string";
  } else if (e.errorName === "VersionAlreadyExists") {
    message = "The site version already exists";
  } else if (e.errorName === "VersionNotFound") {
    message = "The site version could not be found";
  } else if (e.errorName === "VersionLimitExceeded") {
    const {
      versionLimit
    } = e.parameters ?? {};
    const versionLimitPart = versionLimit != null ? ` (Limit: ${versionLimit} versions)` : "";
    message = `The site contains too many versions${versionLimitPart}`;
    tip = "Run the `site version delete` command to delete an old version and try again";
  } else if (e.errorName === "FileCountLimitExceeded") {
    const {
      fileCountLimit
    } = e.parameters ?? {};
    const fileCountLimitPart = fileCountLimit != null ? ` (Limit: ${fileCountLimit} files)` : "";
    message = `The .zip file contains too many files${fileCountLimitPart}`;
    tip = "Reduce the number of files in the production build to below the limit";
  } else if (e.errorName === "FileSizeLimitExceeded") {
    const {
      currentFilePath,
      currentFileSizeBytes,
      fileSizeBytesLimit
    } = e.parameters ?? {};
    const currentFilePathPart = currentFilePath != null ? ` "${currentFilePath}"` : "";
    const currentFileSizePart = currentFileSizeBytes != null ? ` (${prettyBytes(parseInt(currentFileSizeBytes), {
      binary: true
    })})` : "";
    const fileSizeLimitPart = fileSizeBytesLimit != null ? ` (Limit: ${prettyBytes(parseInt(fileSizeBytesLimit), {
      binary: true
    })})` : "";
    message = `The .zip file contains a file${currentFilePathPart}${currentFileSizePart} that is too large${fileSizeLimitPart}`;
    tip = "Ensure all files in the production build are below the size limit";
  } else if (e.errorName === "ScanningInProgress") {
    message = "The website version is being scanned for vulnerabilities and cannot be deployed yet";
    tip = "If you have a `foundry.config.json` file, set the `site.uploadOnly` property to `true` to disable automatic deployment.\n" + "Website versions can be manually deployed from the Website Hosting page in Developer Console after their scans have completed";
  } else {
    if (e instanceof UnknownError) {
      // Include deep inspect of original error
      message = `${e.message}\n\n${inspect(e, {
        depth: 10,
        colors: true
      })}`;
    } else {
      // Include extra info about the original API error
      // https://www.palantir.com/docs/foundry/api/general/overview/errors/
      const {
        errorCode,
        errorName,
        errorInstanceId,
        parameters
      } = e;
      message = `${e.message}\n\n${JSON.stringify({
        errorCode,
        errorName,
        errorInstanceId,
        parameters
      }, null, 2)}`;
    }
  }
  throw new ExitProcessError(1, message, tip, e);
}
function createRequestLoggingFetch(fetchFn = fetch) {
  return function requestLoggingFetch(input, init) {
    const requestLog = typeof input === "string" || input instanceof URL ? `${init?.method ?? "GET"}: ${input.toString().trim()}` :
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
    `${input.method ?? "GET"}: ${input.url.toString().trim()}`;
    consola.trace(requestLog);
    return fetchFn(input, init).then(a => {
      consola.trace(`FINISH ${requestLog}`);
      return a;
    });
  };
}